import React, { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { PathwayTemplate, SavedPathway } from '../../../types/pathway';
import { SelectDropdown } from '../../SelectDropdown';
import { ResetFiltersButton } from '../../ResetFiltersButton';
import { PathwayButton } from '../PathwayButton';
import { SavedPathwayRow } from './SavedPathwayRow';
interface SavedPathwaysViewProps {
  pathways: SavedPathway[];
  templates: PathwayTemplate[];
  onOpen: (pathway: SavedPathway) => void;
  onDuplicate: (pathway: SavedPathway) => void;
  onBuildNew: () => void;
  onUseTemplate: (template: PathwayTemplate) => void;
}
export function SavedPathwaysView({
  pathways,
  templates,
  onOpen,
  onDuplicate,
  onBuildNew,
  onUseTemplate
}: SavedPathwaysViewProps) {
  const [orgFilter, setOrgFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const organizations = useMemo(() => Array.from(new Set(pathways.map(p => p.organization))).sort(), [pathways]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = pathways.filter(pathway => {
      if (orgFilter !== 'all' && pathway.organization !== orgFilter) return false;
      if (needle && !`${pathway.name} ${pathway.organization}`.toLowerCase().includes(needle)) {
        return false;
      }
      return true;
    });
    return filtered.slice().sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'company') return a.organization.localeCompare(b.organization) || a.name.localeCompare(b.name);
      if (sort === 'size') return b.moduleIds.length - a.moduleIds.length;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [pathways, orgFilter, query, sort]);
  const isFiltered = orgFilter !== 'all' || query.trim().length > 0 || sort !== 'recent';
  const resetFilters = () => {
    setOrgFilter('all');
    setQuery('');
    setSort('recent');
  };
  return <section aria-labelledby="saved-pathways-heading" data-id="element-1794" data-mp="28pucdsf0sfo9">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4" data-id="element-1795" data-mp="24p22qy28k8rg">
        <div data-id="element-1796" data-mp="1zqh2wzqwbr3j">
          <h2 id="saved-pathways-heading" className="text-xl font-bold text-gray-900" data-id="element-1797" data-mp="1zr0kgfcmwdh1">
            Your pathways
          </h2>
          <p className="mt-1 max-w-xl text-sm text-gray-600" data-id="element-1798" data-mp="2wiubzikhwu12">
            Pick up something you started, or duplicate a pathway that worked
            for a new prospect.
          </p>
        </div>
        <PathwayButton onClick={onBuildNew} className="whitespace-nowrap px-8" data-id="element-1799" data-mp="2nmbl0bvtrg74">
          <Plus size={18} data-id="element-1800" data-mp="2iotltd7m0fcb" />
          Build a New Pathway
        </PathwayButton>
      </div>

      {pathways.length > 0 && <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-1801" data-mp="3g6k09ft84c5j">
          <label className="relative" data-id="element-1802" data-mp="3bcfqypwhjs1a">
            <span className="sr-only" data-id="element-1803" data-mp="3ip57psbyq9ll">Search pathways</span>
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1804" data-mp="3iqq31k63pwki" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name or company" className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1805" data-mp="3smtczc772zi0" />
          </label>
          <SelectDropdown ariaLabel="Filter pathways by company" scrollMenu value={orgFilter} onChange={setOrgFilter} options={[{
        value: 'all',
        label: 'All companies'
      }, ...organizations.map(org => ({
        value: org,
        label: org
      }))]} data-id="element-1806" data-mp="f7wr1bsz1fsg" />
          <SelectDropdown ariaLabel="Sort pathways" value={sort} onChange={setSort} options={[{
        value: 'recent',
        label: 'Recently updated'
      }, {
        value: 'name',
        label: 'Pathway name'
      }, {
        value: 'company',
        label: 'Company'
      }, {
        value: 'size',
        label: 'Most modules'
      }]} data-id="element-1807" data-mp="t8r5ab2paaz1" />
          <ResetFiltersButton isActive={isFiltered} onReset={resetFilters} data-id="element-1808" data-mp="kcquwr5na6ip" />
        </div>}

      {pathways.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white p-8 shadow-sm" data-id="element-1809" data-mp="3oqf8thrjvb0z">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-1810" data-mp="3osiax05z8um2">No pathways yet</h3>
          <p className="mt-1 max-w-lg text-sm text-gray-600" data-id="element-1811" data-mp="6mdlslh0olrs">
            Start from a proven sequence your team already sells, or build one
            from a conversation you just had.
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2" data-id="element-1812" data-mp="1hyegeh0kb4fg">
            {templates.map(template => <button key={template.id} type="button" onClick={() => onUseTemplate(template)} className="rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-[#30718D] hover:bg-[#30718D]/5" data-id="element-1813" data-mp="1d0mpnqbnlocu">
                <p className="text-sm font-semibold text-gray-900" data-id="element-1814" data-mp="2lzw8dehvvefs">
                  {template.name}
                </p>
                <p className="mt-1 text-xs text-gray-600" data-id="element-1815" data-mp="3emgykbmmhkhr">{template.summary}</p>
              </button>)}
          </div>
        </div> : visible.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-1816" data-mp="3iq6k0s803lyk">
          <p className="text-sm text-gray-600" data-id="element-1817" data-mp="34ukzoycppqch">
            No pathways match those filters.
          </p>
          <button type="button" onClick={resetFilters} className="mt-2 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1818" data-mp="39tfii0aojewl">
            Clear filters
          </button>
        </div> : <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1819" data-mp="g25jotuhlumb">
          <div className="overflow-x-auto" data-id="element-1820" data-mp="b4vwzlyhfx26">
            <table className="w-full table-fixed sm:min-w-[760px]" data-id="element-1821" data-mp="64hok2syyl3m">
              <caption className="sr-only" data-id="element-1822" data-mp="62n2um9vioyh">
                Saved pathways — expand a row to see its modules

              </caption>
              <colgroup data-id="element-1823" data-mp="3t97dift9rblt">
                <col data-id="element-1824" data-mp="3t9fmvxq6nys9" />
                <col className="w-[200px]" data-id="element-1825" data-mp="3t9z4fdbx8l5r" />
                <col className="w-[110px]" data-id="element-1826" data-mp="3oih7r1gxl6ql" />
                <col className="w-[190px]" data-id="element-1827" data-mp="3ohp8ngo4kqvk" />
              </colgroup>
              <thead data-id="element-1828" data-mp="3jius6ldmreem">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-1829" data-mp="3jkflk7o7kraw">
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1830" data-mp="3ekjjidz2n9xz">
                    Pathway
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1831" data-mp="39nawo02zahqe">
                    Audience
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1832" data-mp="1l0spydmrhhs2">
                    Modules
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-right text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1833" data-mp="1u1qxh5wes7fe">
                    <span className="sr-only" data-id="element-1834" data-mp="1z0ajf6b05j7r">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1835" data-mp="11fei7v48ewlz">
                {visible.map(pathway => <SavedPathwayRow key={pathway.id} pathway={pathway} expanded={expandedId === pathway.id} onToggleExpand={() => setExpandedId(expandedId === pathway.id ? null : pathway.id)} onOpen={onOpen} onDuplicate={onDuplicate} data-id="element-1836" data-mp="16dps25uj1747" />)}
              </tbody>
            </table>
          </div>
        </div>}
    </section>;
}